import { useState, useEffect } from 'react';
import { loadAnalytics } from '@/utils/analytics';
import { X, Check, Cookie } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { motion, AnimatePresence } from 'framer-motion';

interface CookieBannerProps {
  onOpenCookiePolicy: () => void;
  forceOpen?: boolean;
  onClose?: () => void;
}

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_ANALYTICS_KEY = 'cookie_analytics';
const SIX_MONTHS_MS = 6 * 30 * 24 * 60 * 60 * 1000;

const CookieBanner = ({ onOpenCookiePolicy, forceOpen = false, onClose }: CookieBannerProps) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    if (forceOpen) {
      setIsVisible(true);
      // Load previous analytics preference
      const analyticsConsent = localStorage.getItem(COOKIE_ANALYTICS_KEY);
      setAnalyticsEnabled(analyticsConsent === 'true');
      return;
    }

    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }

    // Check if consent has expired (6 months)
    try {
      const consentData = JSON.parse(consent);
      if (Date.now() - consentData.timestamp > SIX_MONTHS_MS) {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        localStorage.removeItem(COOKIE_ANALYTICS_KEY);
        const timer = setTimeout(() => setIsVisible(true), 1000);
        return () => clearTimeout(timer);
      }
    } catch {
      // Invalid consent data, show banner
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [forceOpen]);

  const handleAccept = () => {
    const consentData = {
      status: 'accepted',
      timestamp: Date.now(),
      analytics: analyticsEnabled,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    localStorage.setItem(COOKIE_ANALYTICS_KEY, String(analyticsEnabled));
    
    if (analyticsEnabled) {
      loadAnalytics();
    }
    
    setIsVisible(false);
    onClose?.();
  };

  const handleDecline = () => {
    const consentData = {
      status: 'declined',
      timestamp: Date.now(),
      analytics: false,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    localStorage.setItem(COOKIE_ANALYTICS_KEY, 'false');
    
    setIsVisible(false);
    onClose?.();
  };

  const handleClose = () => {
    // Close temporarily without saving - will reappear next visit
    setIsVisible(false);
    onClose?.();
  };

  const handleLearnMore = (e: React.MouseEvent) => {
    e.preventDefault();
    onOpenCookiePolicy();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-4 right-4 z-50 w-[calc(100%-2rem)] max-w-[380px] sm:bottom-6 sm:right-6"
        >
          <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] p-6 border border-border/20">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Cookie className="w-4 h-4 text-[#4b5563]" />
                <span className="text-xs font-medium text-[#4b5563] uppercase tracking-wider">
                  {t('cookieBanner.badge')}
                </span>
              </div>
              <button
                onClick={handleClose}
                className="p-1.5 rounded-full hover:bg-muted transition-colors"
                aria-label={t('cookieBanner.close')}
              >
                <X className="w-4 h-4 text-[#4b5563]" />
              </button>
            </div>

            {/* Title */}
            <h3 className="text-lg font-bold text-[#1a2744] mb-2">
              {t('cookieBanner.title')}
            </h3>

            {/* Description */}
            <p className="text-sm text-[#4b5563] leading-relaxed mb-4">
              {t('cookieBanner.description')}
            </p>

            {/* Learn more link */}
            <button
              onClick={handleLearnMore}
              className="text-[13px] text-[#1a2744] underline hover:no-underline mb-5 inline-block"
              aria-label={t('cookieBanner.learnMoreAriaLabel')}
            >
              {t('cookieBanner.learnMore')}
            </button>

            {/* Cookie toggles */}
            <div className="space-y-2 mb-6">
              {/* Essential cookies - always on */}
              <div className="flex items-center justify-between p-3 bg-[#f9fafb] rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1a2744]">
                    {t('cookieBanner.essential.title')}
                  </p>
                  <p className="text-xs text-[#4b5563]">
                    {t('cookieBanner.essential.description')}
                  </p>
                </div>
                <Switch
                  checked={true}
                  disabled
                  aria-label={t('cookieBanner.essential.title')}
                  className="data-[state=checked]:bg-accent opacity-60 cursor-not-allowed ml-3 flex-shrink-0"
                />
              </div>

              {/* Analytics cookies - toggleable */}
              <div className="flex items-center justify-between p-3 bg-[#f9fafb] rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1a2744]">
                    {t('cookieBanner.analytics.title')}
                  </p>
                  <p className="text-xs text-[#4b5563]">
                    {t('cookieBanner.analytics.description')}
                  </p>
                </div>
                <Switch
                  checked={analyticsEnabled}
                  onCheckedChange={setAnalyticsEnabled}
                  aria-label={t('cookieBanner.analytics.title')}
                  className="data-[state=checked]:bg-accent ml-3 flex-shrink-0"
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={handleDecline}
                className="flex-1 py-3 px-6 rounded-lg border-2 border-[#e5e7eb] text-sm font-semibold text-[#374151] hover:bg-[#f3f4f6] transition-colors"
              >
                {t('cookieBanner.decline')}
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 py-3 px-6 rounded-lg bg-[#1a2744] text-white text-sm font-semibold hover:bg-[#2d3748] transition-colors flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                {t('cookieBanner.accept')}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
